import { supabase } from '@/lib/supabase';
import { resendInviteApi, type ResendChannel } from '@/api/resendInvite';
import type {
  UserListItem,
  OrgMember,
  OrgUserRole,
  ResetMode,
  ResetPasswordResult,
} from '@/types';

export const usersApi = {
  listUsers: async (params?: { search?: string; role?: string }): Promise<UserListItem[]> => {
    let query = supabase
      .from('users')
      .select('*, organizations:organizations!users_organization_id_fkey(name)')
      .order('created_at', { ascending: false });

    if (params?.search) {
      const s = `%${params.search}%`;
      query = query.or(`email.ilike.${s},name.ilike.${s},first_name.ilike.${s}`);
    }

    if (params?.role) {
      query = query.eq('role', params.role);
    }

    const { data, error } = await query;
    if (error) throw error;

    return (data ?? []).map((row: any) => ({
      ...row,
      org_name: row.organizations?.name ?? null,
    })) as UserListItem[];
  },

  listLastLogins: async (): Promise<Record<string, string | null>> => {
    const { data, error } = await supabase.rpc('admin_users_with_login');
    if (error) throw error;
    const map: Record<string, string | null> = {};
    (data ?? []).forEach((r: any) => { map[r.user_id] = r.last_sign_in_at ?? null; });
    return map;
  },

  getUserStats: async (): Promise<{ total_users: number; admin_count: number; student_count: number }> => {
    const { data, error } = await supabase
      .from('users')
      .select('role');
    if (error) throw error;

    const rows = data ?? [];
    return {
      total_users: rows.length,
      admin_count: rows.filter((r: any) => r.role === 'admin').length,
      student_count: rows.filter((r: any) => r.role === 'student').length,
    };
  },

  resendUserInvite: async (userId: string, channel: ResendChannel): Promise<{ sent_to: string }> => {
    return resendInviteApi.send({ target_type: 'user', target_id: userId, channel });
  },

  listByOrg: async (orgId: string): Promise<OrgMember[]> => {
    const { data, error } = await supabase
      .from('users')
      .select('id, auth_user_id, email, name, first_name, last_name, role, created_at')
      .eq('organization_id', orgId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return (data ?? []) as OrgMember[];
  },

  /**
   * Cross-org membership roles from `org_users`. Used by the global Users page
   * to label each user as Owner / Member.
   */
  listOrgRoles: async (): Promise<OrgUserRole[]> => {
    const { data, error } = await supabase
      .from('org_users')
      .select('user_id, organization_id, role, status');
    if (error) throw error;
    return (data ?? []) as OrgUserRole[];
  },

  /**
   * Reset a user's password via the `admin-reset-password` Edge Function.
   * Identity is verified server-side (caller JWT) regardless of the client guard.
   */
  resetPassword: async (args: {
    target_auth_user_id: string;
    mode: ResetMode;
    new_password?: string;
  }): Promise<ResetPasswordResult> => {
    const { data, error } = await supabase.functions.invoke('admin-reset-password', { body: args });
    if (error) throw error;
    if ((data as any)?.error) throw new Error((data as any).error);
    return data as ResetPasswordResult;
  },

  manageAccess: async (args: {
    target_user_id: string;
    action: 'reset_portal_otp' | 'reset_mfa' | 'suspend' | 'restore' | 'cancel';
    reason: string;
  }): Promise<{ ok: true; action: string; affected?: number; channel?: string }> => {
    const { data, error } = await supabase.functions.invoke('admin-user-access', { body: args });
    if (error) throw error;
    if ((data as any)?.error) throw new Error((data as any).error);
    return data as { ok: true; action: string; affected?: number; channel?: string };
  },
};
